import React, { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { Auth } from 'aws-amplify';

import { userApi } from 'api/user';
import { clearSentryUser, logError, setSentryUser } from 'utils/sentryUtils';

import { AuthContext } from './AuthContext';

const userReducer = (user, newUser) => {
    if (!newUser) {
        localStorage.removeItem('user');
        return {};
    }

    return { ...user, ...newUser };
};

const fbmReducer = (account, newAccount) => {
    if (!newAccount) {
        localStorage.removeItem('fbmAccount');
        return {};
    }

    return { ...account, ...newAccount };
};

const famReducer = (account, newAccount) => {
    if (!newAccount) {
        localStorage.removeItem('famAccount');
        return {};
    }

    return { ...account, ...newAccount };
};

const wholesaleReducer = (account, newAccount) => {
    if (!newAccount) {
        localStorage.removeItem('wholesaleAccount');
        return {};
    }

    return { ...account, ...newAccount };
};

const initialUser = JSON.parse(localStorage.getItem('user'));
const initialFbmAccount = JSON.parse(localStorage.getItem('fbmAccount'));
const initialFamAccount = JSON.parse(localStorage.getItem('famAccount'));
const initialWholesaleAccount = JSON.parse(localStorage.getItem('wholesaleAccount'));
const UserContext = createContext();

/** {
 * id,
 * language,
 * facilityID,
 * facilityName,
 * name,
 * canRefundPastDate,
 * canAdjustCount,
 * canAdjustInvFromDesktop,
 * }
 *
 */
function UserProvider({ children }) {
    const { authStatus } = useContext(AuthContext);
    const [user, setUser] = useReducer(userReducer, initialUser || {});
    const [roles, setRoles] = useState([]);
    const [fbmAccount, setFbmAccount] = useReducer(fbmReducer, initialFbmAccount || {});
    const [famAccount, setFamAccount] = useReducer(famReducer, initialFamAccount || {});
    const [wholesaleAccount, setWholesaleAccount] = useReducer(
        wholesaleReducer,
        initialWholesaleAccount || {
            id: 6,
            companyName: 'Go Puff',
            fbmAccountName: 'gopuff',
            facilityIds: [9, 10, 11, 12, 13],
        },
    );

    useEffect(() => {
        if (user?.id) {
            setSentryUser(user.id, user.name);
            localStorage.setItem('user', JSON.stringify(user));
        }
    }, [user]);

    useEffect(() => {
        if (fbmAccount?.id) {
            localStorage.setItem('fbmAccount', JSON.stringify(fbmAccount));
        }
    }, [fbmAccount]);

    useEffect(() => {
        if (famAccount?.partnershipSource) {
            localStorage.setItem('famAccount', JSON.stringify(famAccount));
        }
    }, [famAccount]);

    useEffect(() => {
        if (wholesaleAccount?.id) {
            localStorage.setItem('wholesaleAccount', JSON.stringify(wholesaleAccount));
        }
    }, [wholesaleAccount]);

    const clearUserAndLogOut = async () => {
        setUser(null);
        setFbmAccount(null);
        setFamAccount(null);
        setWholesaleAccount(null);
        clearSentryUser();
        // Set flag to indicate this is an explicit logout, not a session expiration
        sessionStorage.setItem('explicitLogout', 'true');
        sessionStorage.removeItem('loginRedirect');
        try {
            await Auth.signOut();
        } catch (error) {
            console.error('Error signing out:', error);
            logError(error);
        }
    };

    const setLanguage = (language) => {
        setUser({ language });
    };

    const setFacilityIDAndName = ({ facilityID, facilityName }) => {
        setUser({ facilityName, facilityID });
    };

    const setUserAttributes = (user) => {
        if (user.fbm) {
            const { fbmAccountName, id, name } = user;
            setUser({ id, name, fbmAccountName, fbm: true, famPartner: false });
        } else if (user.fam) {
            const { facilities, id, name, partnershipSource } = user;
            setFamAccount({ partnershipSource, facilities });
            setUser({ id, name, partnershipSource, famPartner: true });
        } else {
            const {
                canAdjustCount,
                canAdjustInvFromDesktop,
                canBulkUpdateTaxonomy,
                canOverrideNegativeVariance,
                canRefundPastDate,
                cycleCountManager,
                facilityCluster,
                facilityClusterIds,
                facilityID,
                facilityName,
                hasTimestampTrucksRole,
                homeFacility,
                id,
                language,
                name,
            } = user;
            setUser({
                id,
                language,
                facilityCluster,
                facilityClusterIds,
                facilityID,
                facilityName,
                name,
                canRefundPastDate,
                canAdjustCount,
                canAdjustInvFromDesktop,
                canBulkUpdateTaxonomy,
                fbm: false,
                famPartner: false,
                homeFacility,
                cycleCountManager,
                hasTimestampTrucksRole,
                canOverrideNegativeVariance,
            });
        }
    };

    const getRoles = async () => {
        const res = await userApi.getUsersRoles();
        if (!res.status) {
            // Only logout on 403 Forbidden (user not authorized)
            if (res.msg === 'Forbidden') {
                clearUserAndLogOut();
            }
            return false;
        }

        const userData = res?.data?.user ?? {};
        setUserAttributes(userData);

        setRoles(res?.data?.rolesLong ?? []);
        return true;
    };

    // Automatically fetch roles when user becomes authenticated
    useEffect(() => {
        if (authStatus === 'authenticated') {
            getRoles();
        }
    }, [authStatus]);

    // MM users only
    const getFacilityCluster = () => user?.facilityCluster || null;
    const getFacilityClusterIds = () => user?.facilityClusterIds || null;
    const getFacilityID = () => user?.facilityID || null;
    const getHomeFacility = () => user?.homeFacility || null;
    const canAdjustCount = () => user?.canAdjustCount || false;
    const canRefundPastDate = () => user?.canRefundPastDate || false;
    const getFacilityName = () => user?.facilityName || '';
    const getUserEmail = () => user?.name || '';
    const canChangeCartExperience = () =>
        user?.rolesLong?.any((role) => role.name.endsWith('CHANGE_CART_EXPERIENCE')) ?? false;
    const canAdjustInvFromDesktop = () => user?.canAdjustInvFromDesktop || false;

    return (
        <UserContext.Provider
            value={{
                user,
                setUserAttributes,
                clearUserAndLogOut,
                setLanguage,
                setFacilityIDAndName,
                fbmAccount,
                setFbmAccount,
                getFacilityCluster,
                getFacilityClusterIds,
                getFacilityID,
                getFacilityName,
                getHomeFacility,
                getUserEmail,
                canRefundPastDate,
                canAdjustCount,
                canChangeCartExperience,
                roles,
                canAdjustInvFromDesktop,
                famAccount,
                setFamAccount,
                wholesaleAccount,
                setWholesaleAccount,
                getRoles,
            }}
        >
            {children}
        </UserContext.Provider>
    );
}

export { UserContext, UserProvider };
